import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  Device,
  TransformationService,
  LmapService,
  TestAssembleService,
  TestServerConfigService,
  SchedulerFormat,
  TestMode,
  TestLayerValue
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Observable, Subject } from 'rxjs';
import {
  BertConfigFieldMapping as ConfigFieldMapping,
  IPAddressValue,
  vlanConfigValue
} from './models/model';

@Injectable({
  providedIn: 'root'
})
export class BertSetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  tabDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  testTaskName = 'bert';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  tenantId = 'pass.local';
  controllerConfig = null;
  private getControllerConfig = '/uicomm/getControllerConfiguration';
  private licensePath = '/licensing/licenses';
  private serverConfigPath = '/uicomm/getServerConfig';
  clientToAgentInfo = {
    licenseInfo: null,
    serverConfigInfo: null,
    dmzServerUrl: null,
    activeLicense: 'tf',
    secondaryTestURL: null
  };
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  getControllerConfiguration(): void {
    const url = `${this.getControllerConfig}`;
    this.controllerConfig = this.apiService.post$<any>(url);
  }

  getScheduleTimeStamp(timestamp: string): string {
    if (timestamp.toLowerCase() === 'now') {
      return null; //To start the test immediately -Need to update based on backend value
    } else {
      const currentTimestamp = new Date().getTime();
      if (Number(timestamp) < currentTimestamp) {
        return null;
      } else {
        return new Date(Number(timestamp)).toISOString();
      }
    }
  }

  getLicenseData(): Observable<any> {
    return this.apiService.get$(this.licensePath);
  }

  getServerConfig(): Observable<any> {
    return this.apiService.post$(this.serverConfigPath);
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }

  getOriginatorAgent(): any {
    return this.selectedOriginator;
  }

  setDestinationAgent(device: Device): void {
    this.selectedDestination = device;
  }

  //eslint-disable-next-line
  getAllTestParams(): any {
    this.output['engine-a-info'] = this.getEngineAInfo();
    this.output['bert-parameters'] = this.getBertParameters();
    this.output['group'] = this.getStreamsGroup();
    return this._getFormatOutput();
  }

  getEngineAInfo(): any {
    const engineInfo = this.output[ConfigFieldMapping.ENGINE_A_INFO];
    if (engineInfo) {
      engineInfo['test-ip'] =
        this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4
          ? this.output[ConfigFieldMapping.SRC_IPV4_ADDRESS] ?? engineInfo['test-ip']
          : this.output[ConfigFieldMapping.SRC_IPV6_ADDRESS];
      engineInfo['test-subnet-mask'] =
        this.output[ConfigFieldMapping.SRC_SUBNET] ?? engineInfo['test-subnet-mask'];
      engineInfo['test-gateway'] =
        this.output[ConfigFieldMapping.SRC_GATEWAY] ?? engineInfo['test-gateway'];
    }
    return engineInfo;
  }

  getEngineBInfo(): any {
    const engineInfo = this.output[ConfigFieldMapping.ENGINE_B_INFO];
    if (engineInfo) {
      engineInfo['test-ip'] =
        this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS] ?? engineInfo['destination-ip'];
    }
    return engineInfo;
  }

  getBertParameters(): any {
    const params = {};
    params[ConfigFieldMapping.BERT_TEST_DURATION] =
      this.output[ConfigFieldMapping.BERT_TEST_DURATION];
    // params[ConfigFieldMapping.FRAME_LOSS_INTERVAL] =
    //   this.output[ConfigFieldMapping.FRAME_LOSS_INTERVAL];
    params[ConfigFieldMapping.IP_ADDRESS_TYPE] = this.output[ConfigFieldMapping.IP_ADDRESS_TYPE];
    params[ConfigFieldMapping.LAYER_TO_TEST] = this.output[ConfigFieldMapping.LAYER_TO_TEST];
    params[ConfigFieldMapping.TEST_MODE] = this.output[ConfigFieldMapping.TEST_MODE];
    if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
      params[ConfigFieldMapping.SEND_PRIMING_PACKETS] = this.output[
        ConfigFieldMapping.SEND_PRIMING_PACKETS
      ]
        ? 'true'
        : 'false';
    }
    params['simulated'] = false;
    params[ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS] =
      this.output[ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS];
    params[ConfigFieldMapping.SWAP_MAC_ADDRESS] = this.output[ConfigFieldMapping.SWAP_MAC_ADDRESS];
    params[ConfigFieldMapping.TRANSMIT_INTERFACE] =
      this.output[ConfigFieldMapping.TRANSMIT_INTERFACE];
    return params;
  }

  getStreamsGroup(): any {
    const group = [];
    const streams = [];
    this.output['streams'].forEach(stream => {
      const streamData = this.getStreamData(stream);
      streams.push(streamData);
    });
    const groupObj = {};
    groupObj['name'] = 'My Connection';
    if (this.output[ConfigFieldMapping.DEVICE_SELECTION_METHOD]) {
      groupObj[ConfigFieldMapping.DEVICE_SELECTION_METHOD] =
        this.output[ConfigFieldMapping.DEVICE_SELECTION_METHOD];
      groupObj['destination-type'] = 'Other';
      if (!this.output[ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS]) {
        groupObj['destination-type'] = 'Acterna2802';
      }
    }
    groupObj['streams'] = streams;
    group.push(groupObj);
    return group;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getStreamData(stream: any): any {
    let streamData = {};
    const output = {};
    output[ConfigFieldMapping.STREAM_NUMBER] = stream[ConfigFieldMapping.STREAM_NUMBER];
    output[ConfigFieldMapping.STREAM_NAME] = stream[ConfigFieldMapping.STREAM_NAME];
    output[ConfigFieldMapping.BERT_PATTERN] = this.output[ConfigFieldMapping.BERT_PATTERN];
    output[ConfigFieldMapping.ENABLE_FRAME_DELAY] = true;
    output[ConfigFieldMapping.ERROR_ON_OOS] = stream[ConfigFieldMapping.ERROR_ON_OOS];
    output[ConfigFieldMapping.OVER_HEAD_BYTES] =
      stream[ConfigFieldMapping.OVER_HEAD_BYTES]?.toString();
    output[ConfigFieldMapping.ULR] = stream[ConfigFieldMapping.ULR];
    output[ConfigFieldMapping.LOSS] = stream[ConfigFieldMapping.LOSS];
    output[ConfigFieldMapping.FD] = stream[ConfigFieldMapping.FD];
    output[ConfigFieldMapping.FDV] = stream[ConfigFieldMapping.FDV];
    output[ConfigFieldMapping.BER_THRESHOLD] = stream[ConfigFieldMapping.BER_THRESHOLD];
    streamData = this.removeNullValues(output);
    const l2Config = {};
    const l3IPv4Config = {};
    if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
      // Add fields for IPV4
      l3IPv4Config[ConfigFieldMapping.TOS] = stream[ConfigFieldMapping.TOS];
      l3IPv4Config[ConfigFieldMapping.TTL] = stream[ConfigFieldMapping.TTL];
      if (this.output[ConfigFieldMapping.TEST_MODE] === TestMode.LOOPBACK) {
        // Only for IPV4 + loopback device
        l3IPv4Config[ConfigFieldMapping.DEST_IPV4_ADDRESS] =
          this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS];
      }
      if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3) {
        // Only for IPV4 + L3 device
        l2Config[ConfigFieldMapping.USE_ARP] = this.output[ConfigFieldMapping.USE_ARP];
      } else {
        l2Config[ConfigFieldMapping.USE_ARP] = false;
      }
    }
    l2Config[ConfigFieldMapping.FRAME_TYPE] = stream[ConfigFieldMapping.FRAME_TYPE];
    l2Config[ConfigFieldMapping.REMOTE_VLAN_TAG] = this.output[ConfigFieldMapping.REMOTE_VLAN_TAG];
    l2Config[ConfigFieldMapping.VERIFY_PRIORITY] = this.output[ConfigFieldMapping.VERIFY_PRIORITY];
    l2Config[ConfigFieldMapping.BERT_FRAME_SIZE] =
      stream[ConfigFieldMapping.BERT_FRAME_SIZE] ||
      this.output[ConfigFieldMapping.BERT_FRAME_SIZE]?.toString();
    if (
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2 ||
      (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3 &&
        !this.output[ConfigFieldMapping.USE_ARP])
    ) {
      // Only for L2 + loopback device
      l2Config[ConfigFieldMapping.DESTINATION_MAC] = this.formatMacAddress(
        this.output[ConfigFieldMapping.DESTINATION_MAC]
      );
      l2Config[ConfigFieldMapping.SRC_MAC_ADDRESS] = this.formatMacAddress(
        this.output[ConfigFieldMapping.SRC_MAC_ADDRESS]
      );
      l2Config[ConfigFieldMapping.VERIFY_PRIORITY] =
        this.output[ConfigFieldMapping.VERIFY_PRIORITY];
    }

    if (
      this.output[ConfigFieldMapping.TEST_MODE] === TestMode.LOOPBACK &&
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3 &&
      this.output[ConfigFieldMapping.DEST_SHOW_MAC_LAYER3]
    ) {
      l2Config[ConfigFieldMapping.DESTINATION_MAC] = this.formatMacAddress(
        this.output[ConfigFieldMapping.DESTINATION_MAC]
      );
    }

    if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.SINGLE_VLAN) {
      l2Config[ConfigFieldMapping.SVLAN_PRIORITY] = stream[ConfigFieldMapping.SVLAN_PRIORITY];
      l2Config[ConfigFieldMapping.SVLAN_ID] = this.output[ConfigFieldMapping.SVLAN_ID]?.toString();
      l2Config[ConfigFieldMapping.ETHER_TYPE] = this.output[ConfigFieldMapping.ETHER_TYPE];
    }
    if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.Q_IN_Q) {
      l2Config[ConfigFieldMapping.SVLAN_ID] = this.output[ConfigFieldMapping.SVLAN_ID]?.toString();
      l2Config[ConfigFieldMapping.CVLAN_ID] = this.output[ConfigFieldMapping.CVLAN_ID]?.toString();
      l2Config[ConfigFieldMapping.SVLAN_PRIORITY] = stream[ConfigFieldMapping.SVLAN_PRIORITY];
      l2Config[ConfigFieldMapping.CVLAN_PRIORITY] = stream[ConfigFieldMapping.CVLAN_PRIORITY];
      l2Config[ConfigFieldMapping.ETHER_TYPE] = this.output[ConfigFieldMapping.ETHER_TYPE];
      l2Config[ConfigFieldMapping.CVLAN_ETHER_TYPE] =
        this.output[ConfigFieldMapping.CVLAN_ETHER_TYPE];
    }
    streamData['l2-config'] = this.removeNullValues(l2Config);
    streamData['l3-ipv4-config'] = this.removeNullValues(l3IPv4Config);

    return streamData;
  }

  formatMacAddress(address: string): string {
    if (address) {
      return address.replace(/-/g, ':');
    }
    return address;
  }

  //eslint-disable-next-line
  createLMAPObject(dataModel: any): any {
    const testPointA = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    testPointA['id'] = testPointA?.uniqueId;
    if (testPointB) {
      testPointB['id'] = testPointB?.uniqueId;
    }
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    //ATW-3448 - Make as ondemand test when repeat value is none
    if (!this.output.scheduler?.repeatInterval) {
      this.output[ConfigFieldMapping.RUN_TYPE] = 'on-demand';
    }
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }

  //eslint-disable-next-line
  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj: SchedulerFormat = { ...this.output[ConfigFieldMapping.SCHEDULER] };
    // create the test schedule
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.timeStamp && !schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['viavi-solutions-bert:bert'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };

    const actionOption2 = {
      id: 'role',
      value: 'primary'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption2);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    const tags = this.output.scheduler?.repeatInterval ? tagOptions : null;
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);
    this.lmapService.CONST.resultFormatType = 'v2';
    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // rt results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'rt',
      collectorURL,
      schedObj.repeatInterval,
      schedObj.timeStamp,
      null
    );
    return lmapified;
  }

  //eslint-disable-next-line
  createControllerConfig(testPointA, testPointB, lmapified): any {
    let controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    if (this.output[ConfigFieldMapping.TEST_MODE] !== TestMode.LOOPBACK) {
      const secondaryConfig = JSON.parse(JSON.stringify(lmapified));
      let foundRole = false,
        foundPhyId = false;
      // secondary config is created as a copy of primary config, so need to update for role and phy-id
      for (const x in secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0]
        .option) {
        // update role to secondary
        if (
          !foundRole &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'role'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = 'secondary';
          foundRole = true;
        }
        // remove phy-id object if secondary device doesn't have multiple ports
        if (!foundPhyId && !testPointB.phyId) {
          if (
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x]
              .id === 'phy-id'
          ) {
            foundPhyId = true;
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.splice(
              x,
              1
            );
          }
        }

        // set the value of phy-id
        if (
          !foundPhyId &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'phy-id'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = testPointB.phyId ? testPointB.phyId : ''; // set value if exists, blank otherwise
          foundPhyId = true;
        }
        if (foundRole && foundPhyId) {
          break;
        }
      }

      // case when primary device is single port, so need to add phy-id object for secondary config
      if (!foundPhyId) {
        if (testPointB.phyId) {
          const optionObjIndex =
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.length;
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ] = {};
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].id = 'phy-id';
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].value = testPointB.phyId;
        }
      }
      controllerConfig = this.testAssembleService.addSecondaryTestConfig(
        controllerConfig,
        'secondaryConfig',
        testPointB,
        this.testTaskName,
        secondaryConfig
      );
    }
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    controllerConfig.test['test-list-data']['mode'] = this.output[ConfigFieldMapping.TEST_MODE];
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['test-list-data']['layer'] =
      this.output[ConfigFieldMapping.LAYER_TO_TEST];
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output[ConfigFieldMapping.TECHINICIAN],
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[ConfigFieldMapping.ALIAS],
      comments : this.output.comments
    };
    controllerConfig.test['extended-config']['test'] = {
      mode: this.output[ConfigFieldMapping.TEST_MODE],
      nameOnProbe: 'bert',
      selected: true,
      testType: 'BERT'
    };
    controllerConfig.test['extended-config']['testPoints'] = [
      {
        tag: 'Test Point A',
        selected: true,
        type: 'inventory',
        id: testPointA.id,
        testPortPhyId: testPointA.phyId,
        name: testPointA.nameId,
        vendor: 'viavi',
        model: testPointA.model.model,
        version: testPointA.params.find(param => param.name === 'version').value,
        ip: testPointA.ipaddress,
        port: '443'
      }
    ];
    controllerConfig.test['extended-config']['pluginConfig'] = this.getExtendedPlugingConfig();
    if (this.output[ConfigFieldMapping.TEST_MODE] !== TestMode.LOOPBACK) {
      controllerConfig.test['extended-config']['testPoints'].push({
        tag: 'Test Point B',
        selected: true,
        type: 'inventory',
        id: testPointB.id,
        testPortPhyId: testPointB.phyId,
        name: testPointB.nameId,
        vendor: 'viavi',
        model: testPointB.model?.model,
        version: testPointB.params.find(param => param.name === 'version').value,
        ip: testPointB.ipaddress,
        port: '443'
      });
    }
    //Adding unmanaged device and device properites
    if (this.output[ConfigFieldMapping.TEST_MODE] === TestMode.LOOPBACK) {
      controllerConfig.test['test-list-data']['tpInfo'].push({
        id: testPointB?.id,
        ip: this.getDestinationIPAddress(testPointB),
        type: 'secondary',
        model: testPointB?.type === 'unmanaged' ? 'unmanaged' : testPointB?.model?.model
      });
      controllerConfig.test['extended-config']['testPoints'].push({
        tag: 'Test Point B',
        selected: true,
        type: 'inventory',
        id: testPointB?.id,
        name: testPointB?.name,
        vendor: 'viavi',
        model: testPointB?.type === 'unmanaged' ? 'unmanaged' : testPointB?.model?.model,
        version: testPointB?.params.find(param => param.name === 'version')?.value,
        ip: testPointB?.ipaddress,
        port: '443'
      });
    }

    controllerConfig.test['ipv6'] = 'no';
    return controllerConfig;
  }

  //eslint-disable-next-line
  getDestinationIPAddress(testpoint: any): string {
    if (testpoint?.ipaddress) {
      return testpoint.ipaddress;
    } else {
      if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
        return this.output[ConfigFieldMapping.DESTINATION_MAC];
      } else {
        return this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS];
      }
    }
  }

  getExtendedPlugingConfig(): any {
    const pluginConfig = {
      connections: {
        connections: [{ source: {}, streams: this.getStreamsDetails() }]
      }
    };

    const source = {};
    if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
      source['layer2SrcType'] = this.output[ConfigFieldMapping.DEFINED_ADDRESS];
    } else {
      if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
        source['ipv4SrcType'] = this.output[ConfigFieldMapping.DEFINED_ADDRESS];
      }
    }
    pluginConfig.connections.connections[0].source = source;
    return pluginConfig;
  }
  getStreamsDetails(): any {
    let newStreams = [];
    const streamObj = {};
    const allStreams = this.output[ConfigFieldMapping.STREAMS];
    if (allStreams.length > 0) {
      allStreams.forEach(stream => {
        streamObj['strmNumber'] = stream[ConfigFieldMapping.STREAM_NUMBER];
        streamObj['strmName'] = stream[ConfigFieldMapping.STREAM_NAME];

        if (stream[ConfigFieldMapping.ERROR_ON_OOS] === undefined) {
          streamObj['strmFailOnOOS'] = undefined;
        } else {
          streamObj['strmFailOnOOS'] = stream[ConfigFieldMapping.ERROR_ON_OOS] ? 'true' : 'false';
        }
        streamObj[ConfigFieldMapping.OVER_HEAD_BYTES] = stream[ConfigFieldMapping.OVER_HEAD_BYTES];
        streamObj['strmSlaCir'] = stream[ConfigFieldMapping.ULR];
        streamObj['strmSlaFrameLoss'] = stream[ConfigFieldMapping.LOSS];
        streamObj['strmSlaFrameDelay'] = stream[ConfigFieldMapping.FD];
        streamObj['strmSlaFdv'] = stream[ConfigFieldMapping.FDV];
        streamObj['strmSlaBer'] = stream[ConfigFieldMapping.BER_THRESHOLD];
        streamObj['strmFramePerf'] = stream[ConfigFieldMapping.BERT_FRAME_SIZE];
        streamObj['strmIpv4DiffServVal'] = stream[ConfigFieldMapping.TOS];
        streamObj['strmIpv4TTL'] = stream[ConfigFieldMapping.TTL];
        streamObj['strmFramePerf'] = stream[ConfigFieldMapping.BERT_FRAME_SIZE];
        streamObj['strmCVlanPri'] = stream[ConfigFieldMapping.CVLAN_PRIORITY];
        streamObj['strmSVlanPri'] = stream[ConfigFieldMapping.SVLAN_PRIORITY];
        newStreams = [...newStreams, { ...streamObj }];
      });
      return newStreams;
    }
  }
}
